import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { HistoryPage as HistoryPageShape, hasMessage } from '@cloudsign/shared';
import type { HistoryRow } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { useListState } from '../../lib/useList';
import { ExportButton } from '../../components/ExportButton';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Badge, EmptyRow, FilterBar, PageHeader, Pager, SelectField, SortHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { DateRangePicker, StatusPill, isRangeValid } from '../../components/phase2';
import type { DateRange } from '../../components/phase2';
import { formatDuration, reportsKey, useActiveGroups, useTenantZone } from './api';
import { addDays, daysBetween, todayIn } from '../../lib/zoneTime';

const PAGE_SIZE = 50;
const MAX_DAYS = 366;
const STATUSES = ['all', 'open', 'closed'] as const;
type Status = (typeof STATUSES)[number];

/** Text for a flag or method code, falling back to the code itself when no message exists. */
function codeText(prefix: string, code: string): string {
  const key = `${prefix}.${code}`;
  return hasMessage(key) ? t(key) : code;
}

function Row({ r }: { r: HistoryRow }) {
  return (
    <tr className="align-top">
      <td className="px-4 py-2 font-medium text-slate-900">
        {r.name}
        {r.flags.length > 0 || r.protected ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {r.protected ? <Badge tone="neutral">{t('rpt.history.protected')}</Badge> : null}
            {r.flags.map((f) => (
              <Badge key={f} tone="warn">
                {codeText('rpt.flag', f)}
              </Badge>
            ))}
          </div>
        ) : null}
      </td>
      <td className="px-4 py-2">{r.siteName}</td>
      <td className="px-4 py-2">{r.groupName}</td>
      <td className="px-4 py-2">{r.hostName ?? '-'}</td>
      <td className="whitespace-nowrap px-4 py-2">{formatDateTime(r.signedInAt, r.timeZone)}</td>
      <td className="whitespace-nowrap px-4 py-2">
        {r.signedOutAt ? formatDateTime(r.signedOutAt, r.timeZone) : <StatusPill tone="info">{t('rpt.history.stillIn')}</StatusPill>}
      </td>
      <td className="whitespace-nowrap px-4 py-2">{r.durationMinutes === null ? '-' : formatDuration(r.durationMinutes)}</td>
      <td className="px-4 py-2">
        {codeText('today.method', r.signInMethod)}
        {r.signOutMethod ? <span className="text-slate-600"> / {codeText('today.method', r.signOutMethod)}</span> : null}
      </td>
    </tr>
  );
}

/** Reports > History: every visit in a date range, filtered by site, group, status and search, with CSV and Excel export. */
export function HistoryPage() {
  const canExport = useCan('history.export');
  const zone = useTenantZone();
  const { sites } = useSite();
  const groups = useActiveGroups();
  const list = useListState({ sort: 'signedInAt', order: 'desc' });
  const [siteId, setSiteId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [status, setStatus] = useState<Status>('all');
  const [range, setRange] = useState<DateRange>(() => {
    const today = todayIn(zone);
    return { from: addDays(today, -6), to: today };
  });

  const complete = range.from !== '' && range.to !== '' && isRangeValid(range);
  const tooLong = complete && daysBetween(range.from, range.to) + 1 > MAX_DAYS;
  const ready = complete && !tooLong;
  const filters = {
    siteId: siteId || undefined,
    groupId: groupId || undefined,
    from: range.from,
    to: range.to,
    search: list.search || undefined,
    status,
    sort: list.sort,
    order: list.order,
  };
  const query = { ...filters, page: list.page, pageSize: PAGE_SIZE };
  const q = useQuery({
    queryKey: [...reportsKey, 'history', query],
    enabled: ready,
    queryFn: () => api.get('/reports/history', { query, schema: HistoryPageShape }),
    placeholderData: keepPreviousData,
  });
  const sortProps = { sort: list.sort, order: list.order, onSort: list.toggleSort };
  const items = q.data?.items ?? [];

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title={t('rpt.history.title')}
        description={t('rpt.history.description')}
        actions={canExport ? <ExportButton path="/reports/history/export" query={filters} fileName="history" disabled={!ready} /> : null}
      />

      <FilterBar columns="sm:grid-cols-2 lg:grid-cols-4">
        <TextField label={t('rpt.filter.search')} type="search" value={list.search} onChange={(e) => list.setSearch(e.target.value)} />
        <SelectField
          label={t('rpt.filter.site')}
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('rpt.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('rpt.filter.group')}
          value={groupId}
          onChange={(e) => {
            setGroupId(e.target.value);
            list.resetPage();
          }}
        >
          <option value="">{t('rpt.filter.allGroups')}</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('rpt.filter.status')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as Status);
            list.resetPage();
          }}
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`rpt.status.${s}`)}
            </option>
          ))}
        </SelectField>
        <div className="sm:col-span-2 lg:col-span-4">
          <DateRangePicker
            label={t('rpt.filter.dates')}
            value={range}
            today={new Date(`${todayIn(zone)}T12:00:00`)}
            onChange={(r) => {
              setRange(r);
              list.resetPage();
            }}
          />
        </div>
      </FilterBar>

      {!complete ? <Alert kind="info">{t('rpt.range.needBoth')}</Alert> : null}
      {tooLong ? <Alert>{t('rpt.range.tooLong', { days: MAX_DAYS })}</Alert> : null}

      {!ready ? null : q.isPending ? (
        <Spinner />
      ) : q.error ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <p className="text-sm text-slate-600" aria-live="polite">
            {t('rpt.history.count', { count: q.data?.total ?? 0 })}
          </p>
          <TableWrap caption={t('rpt.history.table')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <SortHeader label={t('rpt.col.name')} field="name" {...sortProps} />
                <SortHeader label={t('rpt.col.site')} field="site" {...sortProps} />
                <SortHeader label={t('rpt.col.group')} field="group" {...sortProps} />
                <Th>{t('rpt.col.host')}</Th>
                <SortHeader label={t('rpt.col.signedIn')} field="signedInAt" {...sortProps} />
                <Th>{t('rpt.col.signedOut')}</Th>
                <Th>{t('rpt.col.duration')}</Th>
                <Th>{t('rpt.col.method')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? <EmptyRow cols={8} /> : items.map((r) => <Row key={r.visitId} r={r} />)}
            </tbody>
          </TableWrap>
          <Pager page={q.data?.page ?? 1} pageSize={PAGE_SIZE} total={q.data?.total ?? 0} onPage={list.setPage} />
        </>
      )}
    </div>
  );
}
